import OperationStatusResponse from '@/models/misc/OperationStatusResponse';
import { TruckloadQuoteRequest } from '@/models/quotes/TruckloadQuoteRequest';
import truckloadQuoteService from '@/services/TruckloadQuoteService';
import { SaveShipmentRequest } from '@/models/shipments/addOrEdit/requests/SaveShipmentRequest';
import { Location } from '@/models/shipments/addOrEdit/Location';
import { Time } from '@/models/shipments/addOrEdit/Time';
import { Package } from '@/models/shipments/addOrEdit/Package';
import { Accessorial } from '@/models/shipments/addOrEdit/Accessorial';
import { TruckloadQuoteData } from '@/models/quotes/TruckloadQuoteData';
import { TruckloadQuoteMixinData } from '@/models/quotes/TruckloadQuoteMixinData';
import { TruckloadQuoteMixinInstance } from '@/models/quotes/TruckloadQuoteMixinInstance';

type TruckloadQuoteMixin = {
    data(): TruckloadQuoteMixinData;
    methods: {
        formatPhoneNumber(this: TruckloadQuoteMixinInstance, phone: string): string;
        formatPhoneOnInput(this: TruckloadQuoteMixinInstance, eventOrValue: Event | string, field: string): void;
        formatZipCode(this: TruckloadQuoteMixinInstance, zipCode: string, country: string): string;
        formatZipOnInput(this: TruckloadQuoteMixinInstance, eventOrValue: Event | string, field: string): void;
        onHazardousClassChange(this: TruckloadQuoteMixinInstance, value: any): void;
        origLocationFromAddressBookSelected(this: TruckloadQuoteMixinInstance, location: any): void;
        destLocationFromAddressBookSelected(this: TruckloadQuoteMixinInstance, location: any): void;
        mapToSaveShipmentRequest(this: TruckloadQuoteMixinInstance, userId: string, accountId: string): SaveShipmentRequest;
        submitTruckloadQuote(this: TruckloadQuoteMixinInstance, orderId: string): Promise<OperationStatusResponse>;
        extractValue(value: string | { value: string; text: string } | null | undefined): string | undefined;
        mapCommodityToPackage(this: TruckloadQuoteMixinInstance, commodity: any, data: TruckloadQuoteData): Package;
        mapOriginLocation(this: TruckloadQuoteMixinInstance, originInfo: any): Location;
        mapDestinationLocation(this: TruckloadQuoteMixinInstance, destinationInfo: any): Location;
    };
};

export const truckloadQuoteMixin: TruckloadQuoteMixin = {
    data(): TruckloadQuoteMixinData {
        return {
            contactInfo: {
                name: '',
                company: '',
                phone: '',
                email: '',
            },
            originInfo: {
                location: '',
                address: '',
                city: '',
                state: '',
                zipCode: '',
                contactName: '',
                contactPhone: '',
                country: 'USA',
                saveToAddressBook: false,
            },
            destinationInfo: {
                location: '',
                address: '',
                city: '',
                state: '',
                zipCode: '',
                contactName: '',
                contactPhone: '',
                country: 'USA',
                saveToAddressBook: false,
            },
            serviceInfo: {
                pickupDate: '',
                deliveryDate: '',
                equipmentType: '',
                flatbedWithTarps: false,
                hazardousMaterials: false,
                perishableGoods: false,
            },
            commodities: [],
            unitsOfMeasure: 'standard',
            additionalNotes: '',
            hazmatClasses: [],
            selectedHazardousClass: null,
        };
    },
    methods: {
        formatPhoneNumber(this: TruckloadQuoteMixinInstance, phone: string): string {
            // remove all non-digit characters
            const cleaned = phone.replace(/\D/g, '');

            // check if we have enough digits
            if (cleaned.length !== 10) return phone;

            // format as (XXX) XXX-XXXX
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        },

        formatPhoneOnInput(this: TruckloadQuoteMixinInstance, eventOrValue: Event | string, field: string) {
            let value: string;

            if (typeof eventOrValue === 'string') {
                value = eventOrValue;
            } else if (eventOrValue && eventOrValue.target) {
                value = (eventOrValue.target as HTMLInputElement).value;
            } else {
                return; // Invalid input, exit early
            }

            const formatted = this.formatPhoneNumber(value);
            if (formatted !== value) {
                if (field === 'contact') {
                    this.contactInfo.phone = formatted;
                } else if (field === 'origin') {
                    this.originInfo.contactPhone = formatted;
                } else if (field === 'destination') {
                    this.destinationInfo.contactPhone = formatted;
                }
            }
        },

        formatZipCode(this: TruckloadQuoteMixinInstance, zipCode: string, country: string): string {
            if (!zipCode) return zipCode;

            if (country === 'USA') {
                // remove all non-digit characters
                const cleaned = zipCode.replace(/\D/g, '');
                if (cleaned.length === 9) {
                    return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
                }
                return cleaned.slice(0, 5);
            } else if (country === 'CAN') {
                // remove all non-alphanumeric characters and convert to uppercase
                const cleaned = zipCode.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                if (cleaned.length === 6) {
                    return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
                }
                return cleaned;
            }

            return zipCode;
        },

        formatZipOnInput(this: TruckloadQuoteMixinInstance, eventOrValue: Event | string, field: string) {
            let value: string;

            if (typeof eventOrValue === 'string') {
                value = eventOrValue;
            } else if (eventOrValue && eventOrValue.target) {
                value = (eventOrValue.target as HTMLInputElement).value;
            } else {
                return; // Invalid input, exit early
            }

            const country = field === 'origin' ? this.originInfo.country : this.destinationInfo.country;
            const formatted = this.formatZipCode(value, country);
            if (formatted !== value) {
                if (field === 'origin') {
                    this.originInfo.zipCode = formatted;
                } else if (field === 'destination') {
                    this.destinationInfo.zipCode = formatted;
                }
            }
        },

        onHazardousClassChange(this: TruckloadQuoteMixinInstance, value: any) {
            if (value) {
                // ensure the value is from the predefined list
                const validOption = this.hazmatClasses.find(
                    (option) => option.value === value.value
                );
                if (!validOption) {
                    this.selectedHazardousClass = null;
                } else {
                    this.serviceInfo.hazardousClass = value.value;
                }
            } else {
                this.serviceInfo.hazardousClass = '';
            }
        },

        origLocationFromAddressBookSelected(this: TruckloadQuoteMixinInstance, location: any) {
            this.originInfo.location = location.company;
            this.originInfo.address = location.address1;
            this.originInfo.city = location.city;
            this.originInfo.state = location.state;
            this.originInfo.zipCode = location.postalCode;
            this.originInfo.contactName = location.contact;
            this.originInfo.contactPhone = location.phone;
            this.originInfo.country = location.country;
        },

        destLocationFromAddressBookSelected(this: TruckloadQuoteMixinInstance, location: any) {
            this.destinationInfo.location = location.company;
            this.destinationInfo.address = location.address1;
            this.destinationInfo.city = location.city;
            this.destinationInfo.state = location.state;
            this.destinationInfo.zipCode = location.postalCode;
            this.destinationInfo.contactName = location.contact;
            this.destinationInfo.contactPhone = location.phone;
            this.destinationInfo.country = location.country;
        },

        mapToSaveShipmentRequest(this: TruckloadQuoteMixinInstance, userId: string, accountId: string): SaveShipmentRequest {
            const data = this as unknown as TruckloadQuoteData;
            const saveShipmentRequest = new SaveShipmentRequest();
            saveShipmentRequest.userId = userId;
            saveShipmentRequest.accountId = accountId;
            saveShipmentRequest.serviceType = 6; // Spot Market Bid

            // Map origin location
            const originLocation = this.mapOriginLocation(data.originInfo);
            saveShipmentRequest.origin = originLocation;

            // Map destination location
            const destinationLocation = this.mapDestinationLocation(data.destinationInfo);
            saveShipmentRequest.destination = destinationLocation;

            // Map pickup and delivery times
            const pickupTime = new Time();
            pickupTime.date = data.serviceInfo.pickupDate;
            saveShipmentRequest.pickupWindow = pickupTime;

            const deliveryTime = new Time();
            deliveryTime.date = data.serviceInfo.deliveryDate;
            saveShipmentRequest.deliveryWindow = deliveryTime;

            saveShipmentRequest.equipmentType = parseInt(data.serviceInfo.equipmentType);

            // Map packages (commodities)
            saveShipmentRequest.packages = data.commodities.map(commodity =>
                this.mapCommodityToPackage(commodity, data)
            );

            // Map accessorials - check if any commodity is hazmat
            const hasHazmatCommodities = data.commodities.some(commodity => commodity.isHazmat);
            saveShipmentRequest.accessorials = [];
            if (hasHazmatCommodities) {
                const hazmatAccessorial = new Accessorial(34, 'Hazmat');
                saveShipmentRequest.accessorials.push(hazmatAccessorial);
            }

            // Add perishable goods info to comments if applicable
            if (data.serviceInfo.perishableGoods) {
                const perishableNote = `Perishable Goods${data.serviceInfo.perishableTemp ? ` - Temperature: ${data.serviceInfo.perishableTemp}°F` : ''}`;
                saveShipmentRequest.comments = saveShipmentRequest.comments
                    ? `${saveShipmentRequest.comments}\n${perishableNote}`
                    : perishableNote;
            }

            // Add additional notes to comments
            if (data.additionalNotes) {
                saveShipmentRequest.comments = saveShipmentRequest.comments
                    ? `${saveShipmentRequest.comments}\n${data.additionalNotes}`
                    : data.additionalNotes;
            }

            // Add contact phone to internal comments
            saveShipmentRequest.internalComments = `Contact Phone: ${this.contactInfo.phone}`;
            saveShipmentRequest.internalComments += `\nContact Email: ${this.contactInfo.email}`;

            return saveShipmentRequest;
        },

        submitTruckloadQuote(this: TruckloadQuoteMixinInstance, orderId: string): Promise<OperationStatusResponse> {
            const data = this as unknown as TruckloadQuoteData;
            const quoteRequest: TruckloadQuoteRequest = {
                contactInfo: {
                    name: data.contactInfo.name,
                    company: data.contactInfo.company,
                    phone: data.contactInfo.phone,
                    email: data.contactInfo.email
                },
                originInfo: {
                    location: data.originInfo.location,
                    address: data.originInfo.address,
                    city: data.originInfo.city,
                    state: data.originInfo.state,
                    zipCode: data.originInfo.zipCode,
                    contactName: data.originInfo.contactName,
                    contactPhone: data.originInfo.contactPhone
                },
                destinationInfo: {
                    location: data.destinationInfo.location,
                    address: data.destinationInfo.address,
                    city: data.destinationInfo.city,
                    state: data.destinationInfo.state,
                    zipCode: data.destinationInfo.zipCode,
                    contactName: data.destinationInfo.contactName,
                    contactPhone: data.destinationInfo.contactPhone
                },
                serviceInfo: {
                    pickupDate: data.serviceInfo.pickupDate,
                    deliveryDate: data.serviceInfo.deliveryDate,
                    equipmentType: (data.serviceInfo.equipmentType as any)?.text,
                    flatbedWithTarps: data.serviceInfo.flatbedWithTarps,
                    hazardousMaterials: data.serviceInfo.hazardousMaterials,
                    perishableGoods: data.serviceInfo.perishableGoods
                },
                commodityInfo: data.commodities.map(commodity => ({
                    ...commodity,
                    weightUnit: 'lbs',
                    dimensionUnit: 'in',
                    // include hazmat details if present
                    ...(commodity.isHazmat && {
                        hazmatDetails: {
                            quantity: typeof commodity.hazmatDetails?.quantity === 'string' ? commodity.hazmatDetails.quantity : commodity.hazmatDetails?.quantity?.text,
                            class: typeof commodity.hazmatDetails?.class === 'string' ? commodity.hazmatDetails.class : commodity.hazmatDetails?.class?.text,
                            packingGroup: typeof commodity.hazmatDetails?.packingGroup === 'string' ? commodity.hazmatDetails.packingGroup : commodity.hazmatDetails?.packingGroup?.text,
                            code: commodity.hazmatDetails?.code,
                            contactName: commodity.hazmatDetails?.contactName,
                            contactPhone: commodity.hazmatDetails?.contactPhone
                        }
                    })
                })) as any,
                additionalNotes: data.additionalNotes,
                requestId: orderId?.toString(),
                timestamp: new Date().toISOString()
            };

            return truckloadQuoteService.submitQuote(quoteRequest);
        },
        extractValue(value: string | { value: string; text: string } | null | undefined): string | undefined {
            if (!value) return undefined;
            return typeof value === 'string' ? value : value.value;
        },
        mapCommodityToPackage(this: TruckloadQuoteMixinInstance, commodity: any, data: TruckloadQuoteData): Package {
            const packageItem = new Package();
            packageItem.description = commodity.name;
            packageItem.numberOfPieces = commodity.pieces;

            // Set weight and dimensions (always in standard units: lbs/inches)
            packageItem.totalWeight = commodity.weight;
            packageItem.length = commodity.length;
            packageItem.width = commodity.width;
            packageItem.height = commodity.height;

            // Set hazardous materials properties from commodity
            if (commodity.isHazmat) {
                packageItem.isHazmat = true;

                // Map hazmat details if they exist
                if (commodity.hazmatDetails) {
                    packageItem.hazmatQty = this.extractValue(commodity.hazmatDetails.quantity);
                    packageItem.hazmatClass = this.extractValue(commodity.hazmatDetails.class);
                    packageItem.hazmatPackageGroup = this.extractValue(commodity.hazmatDetails.packingGroup);
                    packageItem.hazmatCode = commodity.hazmatDetails.code;
                    packageItem.hazmatContactName = commodity.hazmatDetails.contactName;
                    packageItem.hazmatContactPhone = commodity.hazmatDetails.contactPhone;
                }
            }

            packageItem.containerType = 'PLT';
            return packageItem;
        },
        mapOriginLocation(this: TruckloadQuoteMixinInstance, originInfo: any): Location {
            const originLocation = new Location();
            originLocation.company = originInfo.location;
            originLocation.contactName = originInfo.contactName;
            originLocation.country = originInfo.country;
            originLocation.address = originInfo.address;
            originLocation.city = originInfo.city;
            originLocation.state = originInfo.state;
            originLocation.zip = originInfo.zipCode;
            originLocation.contactPhone = originInfo.contactPhone;
            originLocation.saveToAddressBook = originInfo.saveToAddressBook;
            return originLocation;
        },
        mapDestinationLocation(this: TruckloadQuoteMixinInstance, destinationInfo: any): Location {
            const destinationLocation = new Location();
            destinationLocation.company = destinationInfo.location;
            destinationLocation.contactName = destinationInfo.contactName;
            destinationLocation.country = destinationInfo.country;
            destinationLocation.address = destinationInfo.address;
            destinationLocation.city = destinationInfo.city;
            destinationLocation.state = destinationInfo.state;
            destinationLocation.zip = destinationInfo.zipCode;
            destinationLocation.contactPhone = destinationInfo.contactPhone;
            destinationLocation.saveToAddressBook = destinationInfo.saveToAddressBook;
            return destinationLocation;
        }
    }
}; 